import * as XLSX from 'xlsx';
import type { AddressImportRow } from '@domain/entities';

/** Parse un fichier Excel ou CSV et retourne les lignes du répertoire. */
export async function parseAddressImportFile(file: File): Promise<AddressImportRow[]> {
  const buffer = await file.arrayBuffer();
  return parseAddressImportBuffer(buffer);
}

/** Testable : parse un ArrayBuffer déjà chargé. */
export function parseAddressImportBuffer(buffer: ArrayBuffer): AddressImportRow[] {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]!];
  const raw = XLSX.utils.sheet_to_json<Record<string, string>>(ws!, { defval: '' });
  return raw
    .map((r) => ({
      name: (r['Nom *'] ?? r['Nom'] ?? r['name'] ?? '').replace(/ \*$/, '').trim(),
      category: (r['Catégorie *'] ?? r['Catégorie'] ?? r['Categorie'] ?? r['category'] ?? '').replace(/ \*$/, '').trim(),
      city: (r['Ville *'] ?? r['Ville'] ?? r['city'] ?? '').replace(/ \*$/, '').trim(),
      address: r['Adresse'] ?? r['address'] ?? '',
      phone: r['Téléphone'] ?? r['Telephone'] ?? r['phone'] ?? '',
      whatsapp: r['WhatsApp'] ?? r['whatsapp'] ?? '',
    }))
    .filter((r) => r.name.trim() !== '');
}

export interface TemplateRefs {
  categories: string[];
  cities: { name: string; countryName: string }[];
  countries: { code: string; name: string }[];
}

/** Génère un fichier Excel modèle avec les valeurs valides de la base en feuille de référence. */
export function generateAddressImportTemplate(refs: TemplateRefs): Blob {
  const wb = XLSX.utils.book_new();

  // Feuille 1 : modèle à remplir
  const wsModel = XLSX.utils.aoa_to_sheet([
    ['Nom *', 'Catégorie *', 'Pays *', 'Ville *', 'Adresse', 'Téléphone', 'WhatsApp'],
    ['Ex: Clinique Mère-Enfant', 'Santé', 'Côte d\'Ivoire', 'Abidjan', '12 rue des Arts', '+225 07 00 00 00 01', '+225 07 00 00 00 02'],
  ]);
  wsModel['!cols'] = [
    { wch: 32 }, { wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 30 }, { wch: 22 }, { wch: 22 },
  ];
  XLSX.utils.book_append_sheet(wb, wsModel, 'Modèle');

  // Feuille 2 : valeurs de référence (catégories, villes, pays)
  const maxLen = Math.max(refs.categories.length, refs.cities.length, refs.countries.length);
  const refRows: string[][] = [['Catégories valides', 'Villes valides', 'Pays (nom)', 'Pays (code)']];
  for (let i = 0; i < maxLen; i++) {
    refRows.push([
      refs.categories[i] ?? '',
      refs.cities[i] ? `${refs.cities[i]!.name} (${refs.cities[i]!.countryName})` : '',
      refs.countries[i]?.name ?? '',
      refs.countries[i]?.code ?? '',
    ]);
  }
  const wsRef = XLSX.utils.aoa_to_sheet(refRows);
  wsRef['!cols'] = [{ wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, wsRef, 'Références');

  const arr = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as Uint8Array<ArrayBuffer>;
  return new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
